import { AfterViewInit, Component, ElementRef, Input, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { MapApi } from '../../../api/map-api';
import { IMCMap, MCMapConstructor } from '../../../classes/mcmap';
import { IMCMapView, MCMapViewConstructor } from '../../../classes/mcMapView';
import { ESRI } from '../../../esri-loader-util';
import { LayerService } from '../../../services/layer.service';
import { MapService } from '../../../services/map.service';
import { BaseComponent } from '../../base.component';
import { BasemapId } from '../basemap-switcher/basemap-config';

// geographic center of PA
const centerLatitude = 40.875;
const centerLongitude = -77.802783;
const zoomLevel = 5;

let Graphic: __esri.GraphicConstructor;
let GraphicsLayer: __esri.GraphicsLayerConstructor;
let Map: MCMapConstructor;
let MapView: MCMapViewConstructor;
let Point: __esri.PointConstructor;
let Polygon: __esri.PolygonConstructor;
let SimpleFillSymbol: __esri.SimpleFillSymbolConstructor;

/** Overview Map Component - Displays an ESRI map overview of the Commonwealth of PA */
@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-overview-map-ng',
    styleUrls: ['../../sharedStyles/base.scss', './overview-map.component.scss'],
    templateUrl: './overview-map.component.html'
})
export class OverviewMapComponent extends BaseComponent implements AfterViewInit {
    @ViewChild('overviewMapArea') private readonly mapAreaRef: ElementRef<HTMLDivElement>;

    private extentLayer: __esri.GraphicsLayer;
    @Input() mainMapView: NonNullable<__esri.MapView>;

    private readonly zoom: number = zoomLevel;
    private readonly center: number[] = [centerLongitude, centerLatitude];
    private readonly basemap: BasemapId = 'streets-vector';

    constructor(
        vcr: ViewContainerRef,
        private readonly mapApi: MapApi,
        private readonly layerService: LayerService,
        private readonly mapService: MapService
    ) {
        super(vcr);
    }

    /** Reference to the displayed ESRI Map and MapView */
    @Input() map: NonNullable<IMCMap>;
    @Input() mapView: NonNullable<IMCMapView>;
    @Input() mapId: string;

    async ngAfterViewInit() {
        try {
            [Graphic, GraphicsLayer, Map, MapView, Point, Polygon, SimpleFillSymbol] =
                await Promise.all([
                    ESRI.Graphic,
                    ESRI.GraphicsLayer,
                    ESRI.MCMap,
                    ESRI.MCMapView,
                    ESRI.Point,
                    ESRI.Polygon,
                    ESRI.SimpleFill
                ]);
            this.map = Map({ basemap: this.basemap });
            this.mapView = await MapView({
                center: this.center,
                container: this.mapAreaRef.nativeElement,
                map: this.map,
                zoom: this.zoom
            }, this.mapId, this.mapApi, this.layerService);

            this.mapView.constraints = {
                rotationEnabled: false
            };
            this.mapView.on('key-down', function(event) {
                const prohibitedKeys = ['+', '-', 'Shift', '_', '='];
                const keyPressed = event.key;
                if (prohibitedKeys.indexOf(keyPressed) !== -1) {
                    event.stopPropagation();
                }
            });
            this.mapView.on('mouse-wheel', this.cancelEvent);
            this.mapView.on('double-click', this.cancelEvent);
            this.mapView.on('double-click', ['Control'], this.cancelEvent);
            this.mapView.on('drag', this.cancelEvent);
            this.mapView.on('drag', ['Shift'], this.cancelEvent);
            this.mapView.on('drag', ['Shift', 'Control'], this.cancelEvent);

            this.mapView.on('click', async (event) => {
                this.mainMapView.goTo({ center: new Point({ latitude: event.mapPoint.latitude, longitude: event.mapPoint.longitude }) });
                event.stopPropagation();
            });

            this.extentLayer = new GraphicsLayer();
            this.map.add(this.extentLayer);

            this.mapService.mcOverviewMapView = this.mapView;

            if (!this.mainMapView) {
                const parent = await this.getClosestAncestor('mc-map');
                if (parent) {
                    await parent.ready;
                    this.mainMapView = parent.mapView;
                } else {
                    this.readyProblem('Map View Reference not supplied and reference from parent element could not be retrieved.');
                }
            }

            this.mainMapView.watch('extent', async () => {
                if (!this.mainMapView) {
                    return;
                }

                // Get the new extent of the view
                if (this.mainMapView.extent) {
                    this.extentLayer.graphics.removeAll();
                    const polygon = await this.getPolygonFromExtent(this.mainMapView.extent);
                    const extentGraphic = new Graphic({
                        geometry: polygon,
                        symbol: new SimpleFillSymbol({
                            style: 'solid',
                            color: [248, 217, 28, 0.1],
                            outline: {
                                color: [255, 0, 0, 1],
                                width: 1
                              }

                        })
                    });

                    this.extentLayer.add(extentGraphic);
                }
            });
        }
        catch (err) {
            this.readyProblem(err);
            throw err;
        }
        finally {
            this.isReady();
        }
    }

    private readonly cancelEvent = (event) => {
        event.stopPropagation();
    }

    private async getPolygonFromExtent(extent: __esri.Extent): Promise<__esri.Polygon> {
        const polygon = new Polygon({
            spatialReference: extent.spatialReference
        });
        polygon.addRing([
            [extent.xmin, extent.ymin],
            [extent.xmin, extent.ymax],
            [extent.xmax, extent.ymax],
            [extent.xmax, extent.ymin],
            [extent.xmin, extent.ymin]
        ]);
        return polygon;
    }
}
